import { cmsPageImageUrl, unescapeCmsText, type CmsPage } from '@/domain';
import { cn } from '@/lib/cn';
import { Button, Modal, Spinner } from '@/ui/common';

export interface ClaimBonusTermsModalProps {
  open: boolean;
  onClose: () => void;
  page: CmsPage | null;
  isLoading: boolean;
  usersBaseUrl: string;
  accountGuid: string;
  label: (key: string, fallback: string) => string;
}

/** CMS T&Cs modal shown when a lost-bonus offer has PageUrl (legacy promotionsmodalbonus). */
export function ClaimBonusTermsModal({
  open,
  onClose,
  page,
  isLoading,
  usersBaseUrl,
  accountGuid,
  label,
}: ClaimBonusTermsModalProps) {
  const title =
    page?.Title?.trim() || label('Bonus', 'Bonus');
  const htmlBody = unescapeCmsText(page?.Text);
  const imageUrl =
    page?.Image?.trim() && accountGuid
      ? cmsPageImageUrl(usersBaseUrl, accountGuid, page.Id, page.Image)
      : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label={title}
      panelClassName="max-h-[min(90dvh,640px)] w-full max-w-lg overflow-hidden rounded-md bg-bg-surface shadow-lg"
    >
      <div className="flex max-h-[min(90dvh,640px)] flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-border-default px-4 py-3">
          <h2 className="text-[16px] font-bold text-text-primary">{title}</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon="close"
            aria-label={label('Close', 'Close')}
            onClick={onClose}
            className="shrink-0 !min-h-0 !px-2 !py-2"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner size="md" />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="w-full rounded-md object-cover"
                  draggable={false}
                />
              ) : null}
              {htmlBody ? (
                <div
                  className={cn(
                    'prose-promo text-body-m text-text-secondary',
                    '[&_a]:text-text-primary [&_a]:underline',
                    '[&_h1]:mb-2 [&_h1]:text-heading-s [&_h1]:text-text-primary',
                    '[&_h2]:mb-2 [&_h2]:text-heading-s [&_h2]:text-text-primary',
                    '[&_p]:mb-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5',
                  )}
                  dangerouslySetInnerHTML={{ __html: htmlBody }}
                />
              ) : (
                <p className="text-body-m text-text-secondary">
                  {label('TermsAndConditions', 'Terms & conditions apply.')}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="border-t border-border-default px-4 py-3">
          <Button type="button" variant="primary" size="md" fullWidth onClick={onClose}>
            {label('BonusAgree', 'I agree')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
